import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import API from 'api/API';
import { UploadFileParams } from 'api/Endpoints/BulkUploadEndpoint';
import { UploadType, UploadTypeMapping } from 'api/Responses/BulkUploadResponses';
import { useLanguage } from 'context/AppContext';
import { DataUploadFilesType, getDataUploadSaveFilePath, UpdatedFileType } from 'pages/DataUpload/DataUpload.helpers';
import { FileService, getGSPath } from 'services/FileService';

interface UseDataUpload {
  uploadedFiles: DataUploadFilesType;
  countOfUploadedFiles: number;
  addFiles: (newFiles: File[]) => Promise<void>;
  deleteFile: (id: string) => void;
  reset: () => void;
  submitFiles: () => Promise<void>;
  updateDataUploadFileType: (id: string, type: UploadTypeMapping) => void;
}

const useDataUpload = (): UseDataUpload => {
  const [uploadedFiles, setUploadedFiles] = useState<DataUploadFilesType>({});
  const countOfUploadedFiles = useMemo(() => Object.keys(uploadedFiles).length, [uploadedFiles]);
  const [language] = useLanguage();

  const updateDataUploadFileType = useCallback(
    (id: string, type: UploadTypeMapping) => {
      const updatedUploadedFiles = structuredClone(uploadedFiles);
      const file = updatedUploadedFiles[id];
      const updatedFile = Object.assign(file, { dataUploadFileType: type }) as UpdatedFileType;
      updatedUploadedFiles[id] = updatedFile;
      setUploadedFiles(updatedUploadedFiles);
    },
    [uploadedFiles],
  );

  const addFiles = useCallback(
    async (newFiles: File[]) => {
      const updatedUploadedFiles = structuredClone(uploadedFiles);

      await Promise.allSettled(
        newFiles.map(async file => {
          const uuid = uuidv4();
          const dataUploadSaveFilePath = getDataUploadSaveFilePath(uuid, file.name);

          await FileService.saveFile(dataUploadSaveFilePath, file);

          const gsPath = getGSPath(dataUploadSaveFilePath);

          const updatedFile = {
            file: file,
            dataUploadFileType: UploadTypeMapping.DEVICE_DATA,
            gsLocation: gsPath,
          } as UpdatedFileType;

          updatedUploadedFiles[uuid] = updatedFile;
        }),
      );
      setUploadedFiles(updatedUploadedFiles);
    },
    [uploadedFiles],
  );

  const deleteFile = useCallback(
    (id: string) => {
      const updatedUploadedFiles = structuredClone(uploadedFiles);
      delete updatedUploadedFiles[id];
      setUploadedFiles(updatedUploadedFiles);
    },
    [uploadedFiles],
  );

  const reset = () => {
    setUploadedFiles({});
  };

  const submitFiles = async () => {
    await Promise.allSettled(
      Object.keys(uploadedFiles).map(async id => {
        const file = uploadedFiles[id];
        const uploadFileBody = {
          gs_location: file.gsLocation,
          upload_type: UploadType[file.dataUploadFileType as keyof typeof UploadType],
        } as UploadFileParams;
        try {
          await API.bulkUpload.uploadFile(uploadFileBody);
          toast.success(language.DataUpload.fileUpload.success.upload);
        } catch (error) {
          API.errorHandler(error);
        }
      }),
    );
  };

  return {
    updateDataUploadFileType,
    uploadedFiles,
    addFiles,
    deleteFile,
    reset,
    submitFiles,
    countOfUploadedFiles,
  };
};

export default useDataUpload;
